import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Plus, X, CheckCircle2, RotateCcw, Trash2, UserCog, Users } from "lucide-react";
import { fmtRoDate } from "../lib/dateFmt";
import { useConfirm } from "./ConfirmProvider";
import { useAuth } from "../context/AuthContext";

const PRIORITY_OPTIONS = [
  { value: "urgent", label: "⚡ URGENT" },
  { value: "high", label: "🔴 Prioritate mare" },
  { value: "normal", label: "🟡 Normal" },
  { value: "low", label: "🟢 Scăzut" },
];
const STATUS_LABEL = { pending: "În așteptare", done: "Executat" };

const emptyForm = { title: "", description: "", assigned_person_ids: [], assigned_user_ids: [], priority: "normal", due_date: "" };

export default function TasksAdminPanel() {
  const { user, isAdmin } = useAuth();
  const confirm = useConfirm();
  const [tasks, setTasks] = useState([]);
  const [persons, setPersons] = useState([]);
  const [users, setUsers] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [filter, setFilter] = useState("pending"); // pending | done | all
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, p, u] = await Promise.all([
        api.get("/tasks", { params: filter !== "all" ? { status: filter } : {} }),
        api.get("/persons-overview").catch(() => ({ data: [] })),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setTasks(t.data || []);
      setPersons(p.data || []);
      setUsers(u.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la încărcare");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const personOptions = useMemo(() => (persons || [])
    .filter((p) => p.active !== false)
    .map((p) => ({ id: p.id, label: `${p.nume || ""} ${p.prenume || ""}`.trim() || "(fără nume)" }))
    .sort((a, b) => a.label.localeCompare(b.label, "ro")), [persons]);

  const userOptions = useMemo(() => (users || [])
    .filter((u) => u.id !== user?.id)  // ascunzi pe tine
    .map((u) => ({ id: u.id, label: u.name || u.username, role: u.role }))
    .sort((a, b) => {
      if (a.role !== b.role) return a.role === "admin" ? -1 : 1;
      return a.label.localeCompare(b.label, "ro");
    }), [users, user?.id]);

  const togglePerson = (pid) => setForm((f) => ({
    ...f,
    assigned_person_ids: f.assigned_person_ids.includes(pid) ? f.assigned_person_ids.filter((x) => x !== pid) : [...f.assigned_person_ids, pid],
  }));
  const toggleUser = (uid) => setForm((f) => ({
    ...f,
    assigned_user_ids: f.assigned_user_ids.includes(uid) ? f.assigned_user_ids.filter((x) => x !== uid) : [...f.assigned_user_ids, uid],
  }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titlul este obligatoriu"); return; }
    if ((form.description || "").trim().length < 10) { toast.error("Descrierea este obligatorie — minim 10 caractere"); return; }
    if (form.assigned_person_ids.length === 0 && form.assigned_user_ids.length === 0) {
      toast.error("Selectează cel puțin un destinatar (angajat sau admin)"); return;
    }
    try {
      await api.post("/tasks", {
        title: form.title.trim(),
        description: form.description?.trim() || null,
        assigned_person_ids: form.assigned_person_ids,
        assigned_user_ids: form.assigned_user_ids,
        priority: form.priority,
        due_date: form.due_date || null,
      });
      const total = form.assigned_person_ids.length + form.assigned_user_ids.length;
      toast.success(`Task atribuit la ${total} destinatar${total > 1 ? "i" : ""}`);
      setOpenDialog(false);
      setForm(emptyForm);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Eroare la salvare"); }
  };

  const remove = async (t) => {
    if (!(await confirm({ body: `Ștergi task-ul „${t.title}"?`, danger: true }))) return;
    await api.delete(`/tasks/${t.id}`);
    toast.success("Șters");
    await load();
  };

  const reopen = async (t) => {
    await api.patch(`/tasks/${t.id}/reopen`);
    toast.success("Redeschis");
    await load();
  };

  if (!isAdmin && (user?.role || "").toLowerCase() !== "magazioner") return null;

  return (
    <div className="card-flat p-4" data-testid="tasks-admin-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <div className="font-display font-bold text-lg">📋 Task-uri date</div>
          <p className="text-xs text-zinc-500">Atribuie task-uri angajaților sau altor admini și urmărește execuția.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-8 w-[160px]" data-testid="tasks-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">În așteptare</SelectItem>
              <SelectItem value="done">Executate</SelectItem>
              <SelectItem value="all">Toate</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => { setForm(emptyForm); setOpenDialog(true); }} data-testid="task-create-btn">
            <Plus size={14} className="mr-1"/> Atribuie task
          </Button>
        </div>
      </div>

      {loading && <div className="text-xs text-zinc-500">Se încarcă…</div>}
      {!loading && tasks.length === 0 && <div className="text-sm text-zinc-500 py-4 text-center">Niciun task.</div>}
      <div className="space-y-2">
        {tasks.map((t) => {
          const done = t.status === "done";
          const allAssignees = [
            ...(t.assigned_names || []).map((n) => ({ label: n, icon: "👤" })),
            ...(t.assigned_user_names || []).map((n, i) => ({ label: n, icon: (t.assigned_user_roles || [])[i] === "admin" ? "⚙️" : "🧑‍💼" })),
          ];
          return (
            <div key={t.id} className={`rounded-md border p-3 ${done ? "bg-emerald-50/40 border-emerald-200" : "bg-white border-zinc-200"}`} data-testid={`admin-task-${t.id}`}>
              <div className="flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded font-bold ${done ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                      {STATUS_LABEL[t.status] || t.status}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 font-bold">
                      {PRIORITY_OPTIONS.find((p) => p.value === (t.priority || "normal"))?.label || "Normal"}
                    </span>
                    {t.due_date && <span className="text-[11px] text-zinc-600">⏰ {fmtRoDate(t.due_date)}</span>}
                  </div>
                  <div className={`font-semibold text-sm mt-1 ${done ? "line-through text-zinc-500" : ""}`}>{t.title}</div>
                  {t.description && <div className="text-xs text-zinc-600 mt-1 whitespace-pre-wrap">{t.description}</div>}
                  <div className="text-[11px] text-zinc-600 mt-2 flex flex-wrap gap-x-2 gap-y-1">
                    <span className="inline-flex items-center gap-1"><Users size={11}/> {allAssignees.length > 0 ? allAssignees.map((a) => `${a.icon} ${a.label}`).join(", ") : "—"}</span>
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-1">
                    Dat de {t.created_by_name || "—"} la {fmtRoDate((t.created_at || "").slice(0, 10))}
                  </div>
                  {done && (
                    <div className="text-[11px] text-emerald-700 mt-1 font-medium inline-flex items-center gap-1">
                      <CheckCircle2 size={12}/> Executat de <strong>{t.completed_by_name || "—"}</strong> la {fmtRoDate((t.completed_at || "").slice(0, 10))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {done && <Button size="sm" variant="ghost" onClick={() => reopen(t)} title="Redeschide" data-testid={`task-reopen-${t.id}`}><RotateCcw size={14}/></Button>}
                  <Button size="sm" variant="ghost" onClick={() => remove(t)} className="text-red-600" title="Șterge" data-testid={`task-remove-${t.id}`}><Trash2 size={14}/></Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-2xl" data-testid="task-create-dialog">
          <DialogHeader><DialogTitle>Atribuie task nou</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Titlu<span className="text-red-600">*</span></Label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="ex: Verifică contractul lui Pope" data-testid="task-title" />
            </div>
            <div>
              <Label>Descriere<span className="text-red-600">*</span> <span className="text-[11px] text-zinc-500 font-normal">(minim 10 caractere)</span></Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Descrie clar ce trebuie făcut (minim 10 caractere)…" data-testid="task-description" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Prioritate</Label>
                <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                  <SelectTrigger data-testid="task-priority"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Termen (opțional)</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))} data-testid="task-due-date" />
              </div>
            </div>
            <div>
              <Label className="inline-flex items-center gap-1"><UserCog size={14}/> Admini / utilizatori</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 mt-1 flex flex-wrap gap-1.5" data-testid="task-users">
                {userOptions.length === 0 && <div className="text-xs text-zinc-500">Niciun utilizator</div>}
                {userOptions.map((u) => {
                  const sel = form.assigned_user_ids.includes(u.id);
                  return (
                    <button
                      key={u.id} type="button"
                      onClick={() => toggleUser(u.id)}
                      className={`text-xs px-2 py-1 rounded border ${sel ? "bg-blue-600 text-white border-blue-600" : "bg-white border-zinc-300 hover:border-blue-300"}`}
                      data-testid={`task-user-${u.id}`}
                    >
                      {sel && <CheckCircle2 size={11} className="inline mr-1"/>}
                      {u.role === "admin" ? "⚙️ " : "🧑‍💼 "}{u.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label className="inline-flex items-center gap-1"><Users size={14}/> Angajați</Label>
              <div className="max-h-40 overflow-y-auto border rounded p-2 mt-1 flex flex-wrap gap-1.5" data-testid="task-persons">
                {personOptions.length === 0 && <div className="text-xs text-zinc-500">Niciun angajat</div>}
                {personOptions.map((p) => {
                  const sel = form.assigned_person_ids.includes(p.id);
                  return (
                    <button
                      key={p.id} type="button"
                      onClick={() => togglePerson(p.id)}
                      className={`text-xs px-2 py-1 rounded border ${sel ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-zinc-300 hover:border-emerald-300"}`}
                      data-testid={`task-person-${p.id}`}
                    >
                      {sel && <CheckCircle2 size={11} className="inline mr-1"/>}
                      {p.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-zinc-500 mt-1">Pentru a vedea task-ul pe dashboard, angajații trebuie să aibă cont activ.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>Anulează</Button>
            <Button onClick={save} data-testid="task-save">Atribuie</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
